import { alpha, Box, BoxProps, ButtonBase, CircularProgress, IconButton } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import SendSvg from 'Assets/icons/send.svg';
import clsx from 'clsx';
import EmojiPicker from 'emoji-picker-react';
import PgIcon from 'Components/PgIcon';
import PopoverWrapper from 'Components/PopoverWrapper';
import TextFieldWMention from 'Components/TextFieldWMention';
import { striptedData, textFieldWMentionHelpers } from 'Components/TextFieldWMention/helpers';
import { MAX_FILE_SIZE } from 'Features/ReactFormFields/ImagePicker';
import useShowToast from 'Hooks/useShowToast';
import useVideoUpload from 'Hooks/useVideoUpload';
import PictureModel from 'Models/Picture';
import { Picture } from 'Models/Picture/@types';
import React, { FC, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { BORDER_BLACK, COLOR } from 'Theme/themeConstants';
import helpers from 'Utils/helpers';
import { ACCEPTED_FILE_TYPES, isImageFile } from 'Utils/fileHelpers';
import { TDiscussionSendMessageFn } from './useDiscussion';
import { ChatContext } from 'Contexts/ChatContext';
import VideoUploadPreview from './VideoUploadPreview';
import FileUploadPreview from './FileUploadPreview';

export type PendingUpload = {
    b64: string;
    isLoading: boolean;
    fileName: string;
    isImage: boolean;
};

export interface ChatInputProps extends BoxProps {
    parentMessageId?: string /** this will be present if input field is used for reply */;
    sendButtonClassName?: string;
    sendMessage: TDiscussionSendMessageFn;
    isDisabled?: boolean;
    rows?: number;
    discussionId: string;
}

const ChatInput: FC<ChatInputProps> = (props) => {
    const classes = useStyles();
    const { sendMessage, parentMessageId, className, isDisabled, sendButtonClassName, rows = 3, discussionId, ...boxProps } = props;
    const { isAdmin } = useContext(ChatContext);
    const [currMessage, setCurrMessage] = useState<string>('');
    const [images, setImages] = useState<Array<Picture | PendingUpload>>([]);
    const [error, setError] = useState<string>();
    const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLElement | null>(null);
    const [cursorPosition, setCursorPosition] = useState<number>(0);

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Video upload management
    const {
        videos,
        startUpload: startVideoUpload,
        cancelUpload: cancelVideoUpload,
        retryUpload: retryVideoUpload,
        removeVideo,
        getCompletedVideos,
        isUploading: isVideoUploading,
        clearVideos,
        validateVideoFile,
    } = useVideoUpload();
    const handleSubmit = () => {
        const val = ``.concat(currMessage);
        const completedVideos = getCompletedVideos();
        const uploadedImages = images.filter((f): f is Picture => 'url' in f && !!(f as Picture).url);

        if (currMessage && val.replace(/\s/g, '')) {
            const parsedMentionMessage = val.split(' ').map(
                (m) =>
                    new Promise((res) => {
                        const isMention = /\B@[a-z0-9_-]+/gi.test(m);
                        if (!isMention) res(m);
                        const mention = m.slice(1);
                        textFieldWMentionHelpers.fetchAutocomplete(discussionId, mention, (data) => {
                            if (!data || !data.length || data?.[0]?.display !== mention) res(m);
                            res(striptedData(data?.[0]?.display ?? '', data[0]?.id as string));
                        });
                    }),
            );
            Promise.all(parsedMentionMessage).then((val) =>
                sendMessage(
                    textFieldWMentionHelpers.stripMetaData(val.join(' '), ['profileImg']),
                    parentMessageId,
                    uploadedImages,
                    completedVideos.length > 0 ? completedVideos : undefined,
                )
                    .then(() => {
                        setError(undefined);
                        setCurrMessage('');
                        setImages([]);
                        clearVideos();
                    })
                    .catch(() => setError('Message was not sent successfully.')),
            );
        } else if (uploadedImages.length > 0 || completedVideos.length > 0) {
            sendMessage(currMessage, parentMessageId, uploadedImages, completedVideos.length > 0 ? completedVideos : undefined)
                .then(() => {
                    setError(undefined);
                    setCurrMessage('');
                    setImages([]);
                    clearVideos();
                })
                .catch(() => setError('Message was not sent successfully.'));
        }
    };

    const showToast = useShowToast();

    /**
     * Captures cursor position on mousedown (before blur) and opens emoji picker on click
     */
    const handleEmojiButtonMouseDown = useCallback(() => {
        if (textareaRef.current) {
            setCursorPosition(textareaRef.current.selectionStart ?? currMessage.length);
        } else {
            setCursorPosition(currMessage.length);
        }
    }, [currMessage.length]);

    const handleOpenEmojiPicker = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setEmojiAnchorEl(event.currentTarget);
    }, []);

    /**
     * Closes emoji picker
     */
    const handleCloseEmojiPicker = useCallback(() => {
        setEmojiAnchorEl(null);
    }, []);

    /**
     * Inserts selected emoji at cursor position and closes picker
     */
    const handleEmojiClick = useCallback(
        (_event: React.MouseEvent, emojiData: { emoji: string }) => {
            const emoji = emojiData.emoji;
            const before = currMessage.slice(0, cursorPosition);
            const after = currMessage.slice(cursorPosition);
            const newMessage = before + emoji + after;
            setCurrMessage(newMessage);
            setEmojiAnchorEl(null);
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    const newPosition = cursorPosition + emoji.length;
                    textareaRef.current.setSelectionRange(newPosition, newPosition);
                }
            }, 0);
        },
        [currMessage, cursorPosition],
    );

    /**
     * Handles video file upload
     */
    const handleVideoUpload = async (file: File) => {
        try {
            const validation = validateVideoFile(file);
            if (!validation.isValid) {
                showToast(validation.error || 'Invalid video file', undefined, 'error');
                return;
            }

            await startVideoUpload(file);
        } catch (error: any) {
            showToast(error?.message || 'Failed to start video upload', undefined, 'error');
        }
    };

    /**
     * Handles non-video file upload (images and other files). All go to Picture upload API.
     */
    const handleFileUpload = async (file: File): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (file.size / 1024 > MAX_FILE_SIZE) {
                showToast('Oops! Choose a file less than 10MB!', undefined, 'error');
                reject(new Error(`File too large: ${file.name}`));
                return;
            }

            const isImage = file.type?.startsWith('image/') ?? false;
            const reader = new FileReader();
            let fileIndex: number | undefined;

            reader.onload = async () => {
                try {
                    setImages((i) => {
                        fileIndex = i.length;
                        return [
                            ...i,
                            {
                                b64: reader.result as string,
                                isLoading: true,
                                fileName: file.name,
                                isImage,
                            },
                        ];
                    });

                    const uploaded = await PictureModel.upload({
                        base64: reader.result as string,
                        name: file.name,
                    });

                    setImages((i) => {
                        if (fileIndex === undefined) return i;
                        return [...i.slice(0, fileIndex), uploaded, ...i.slice(fileIndex + 1)];
                    });
                    resolve();
                } catch (err) {
                    if (fileIndex !== undefined) {
                        setImages((i) => i.filter((_, idx) => idx !== fileIndex));
                    }
                    reject(err);
                }
            };

            reader.onerror = () => {
                reject(new Error('Failed to read file'));
            };

            reader.readAsDataURL(file);
        });
    };

    /**
     * Handles file selection from input
     */
    const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = event.target;
        if (!files || files.length === 0) return;

        try {
            const fileArray = Array.from(files);
            const uploadPromises = fileArray.map((file: File) => {
                if (file.type?.includes('video')) {
                    return handleVideoUpload(file);
                }
                return handleFileUpload(file);
            });

            await Promise.all(uploadPromises);
        } catch (error) {
            console.error('Error handling file uploads:', error);
        } finally {
            // Reset input to allow selecting the same file again
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleFileUploadClick = () => {
        fileInputRef.current?.click();
    };

    /**
     * Handles image removal
     */
    const handleImageClick = (index: number) => () => {
        setImages((i) => i.filter((_, i) => i !== index));
    };

    /**
     * Checks if any upload is in progress
     */
    const isLoading = useMemo(() => {
        const imagesLoading = images.some((img) => !!(img as any)?.isLoading);
        const videosUploading = isVideoUploading();
        return imagesLoading || videosUploading;
    }, [images, isVideoUploading]);

    return (
        <Box className={clsx(classes.container, className)} {...boxProps}>
            <Box display="flex" alignItems="flex-start" gridGap={16}>
                <Box className={classes.imageInputWrap} component={ButtonBase} onClick={handleFileUploadClick}>
                    <PgIcon icon="icon-plus" styleClass={classes.icon} />
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={ACCEPTED_FILE_TYPES}
                        onChange={handleChange}
                        className={classes.imageInput}
                    />
                </Box>

                <TextFieldWMention
                    fetchSuggestions={(q, c) => textFieldWMentionHelpers.fetchAutocomplete(discussionId, q, c)}
                    onChange={(e) => setCurrMessage(e.target.value)}
                    value={currMessage || ''}
                    inputRef={textareaRef}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSubmit();
                        }
                    }}
                    placeholder="Type your message here"
                    disabled={isDisabled}
                    className={clsx(classes.input)}
                    helperText={error}
                    error={!!error}
                />
            </Box>
            {(!!images.length || !!videos.length) && (
                <Box display="flex" alignItems="center" gridGap={8} mt={1} flexWrap="wrap">
                    {images.map((item, idx) => {
                        const isPending = 'isLoading' in item;
                        const isImage = isPending ? (item as PendingUpload).isImage : isImageFile(item as Picture);

                        if (!isImage) {
                            const fileName = isPending ? (item as PendingUpload).fileName : (item as Picture).name ?? 'file';
                            const isLoading = isPending && (item as PendingUpload).isLoading;
                            return (
                                <FileUploadPreview key={`file-${idx}`} fileName={fileName} isLoading={isLoading} onRemove={handleImageClick(idx)} />
                            );
                        }

                        const imgSrc = isPending ? (item as PendingUpload).b64 : helpers.getPictureUrl(item as Picture);
                        const imgName = isPending ? (item as PendingUpload).fileName : (item as Picture).name ?? 'loading';
                        const isImageLoading = isPending && (item as PendingUpload).isLoading;
                        return (
                            <Box
                                key={`img-${idx}`}
                                component={ButtonBase}
                                className={classes.imgThumbWrap}
                                onClick={isImageLoading ? undefined : handleImageClick(idx)}
                                style={{ cursor: isImageLoading ? 'default' : undefined }}
                            >
                                <img src={imgSrc} alt={imgName} className={classes.imgThumb} />
                                <Box
                                    id={`imageThumbOverlay-${idx}`}
                                    className={classes.closeIconWrap}
                                    style={{ display: isImageLoading ? 'grid' : undefined }}
                                >
                                    {isImageLoading ? <CircularProgress size={10} /> : <PgIcon icon="icon-close" styleClass={classes.icon} />}
                                </Box>
                            </Box>
                        );
                    })}

                    {/* Video previews */}
                    {videos.map((video) => (
                        <VideoUploadPreview
                            key={video.id}
                            video={video}
                            onRemove={() => removeVideo(video.id)}
                            onCancel={video.status === 'uploading' ? () => cancelVideoUpload(video.id) : undefined}
                            onRetry={video.status === 'failed' ? () => retryVideoUpload(video.id) : undefined}
                        />
                    ))}
                </Box>
            )}
            <IconButton
                aria-label="Add emoji"
                disabled={isDisabled || isLoading}
                className={clsx(classes.emojiButton, { [classes.emojiButtonError]: !!error })}
                onMouseDown={handleEmojiButtonMouseDown}
                onClick={handleOpenEmojiPicker}
            >
                <PgIcon icon="icon-smiley" styleClass={classes.emojiIcon} />
            </IconButton>
            <IconButton
                disabled={isDisabled || isLoading}
                className={clsx(classes.sendButton, sendButtonClassName, { [classes.active]: !!currMessage, [classes.errorSendButton]: !!error })}
                onClick={handleSubmit}
            >
                <img src={SendSvg} height="20px" />
            </IconButton>
            <PopoverWrapper
                id="chat-emoji-picker"
                anchorEl={emojiAnchorEl}
                onClose={handleCloseEmojiPicker}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                maxHeight={500}
            >
                <EmojiPicker onEmojiClick={handleEmojiClick} searchPlaceholder="Search emoji" pickerStyle={{ width: '350px', height: '450px' }} />
            </PopoverWrapper>
        </Box>
    );
};

const useStyles = makeStyles<Theme>((theme) => {
    return createStyles({
        container: {
            width: '100%',
            position: 'relative',
            borderRadius: 4,
            padding: theme.spacing(1.5, 1.75),
            border: BORDER_BLACK,
            backgroundColor: theme.palette.common.white,
        },
        active: {
            '& svg': {
                fill: `${theme.palette.secondary.main} !important`,
            },
            '& path': {
                fill: `${theme.palette.secondary.main} !important`,
                fillOpacity: `1 !important`,
            },
        },
        input: {
            // background: '#fff',
            // borderRadius: 4,
            // padding: theme.spacing(1.5, 1.75),
            // paddingRight: 45,
            // '& textarea': {
            //     borderRadius: 4,
            //     padding: theme.spacing(1.5, 1.75),
            // },
            '& textarea, & textarea:focus': {
                border: 'none',
                outline: 'none',
            },
        },
        errorSendButton: {
            bottom: `24px !important`,
        },
        emojiButton: {
            position: 'absolute',
            right: theme.spacing(5.5),
            bottom: 0,
            padding: theme.spacing(1, 1),
        },
        emojiIcon: {
            fontSize: theme.spacing(2.5),
            WebkitTextStrokeWidth: '1px',
        },
        emojiButtonError: {
            bottom: `24px !important`,
        },
        sendButton: {
            position: 'absolute',
            right: theme.spacing(0),
            bottom: 0,
            padding: theme.spacing(1, 1.6),
        },
        // outline: {
        //     border: `1px solid rgba(0,0,0,0.5)`,
        //     '& hover': {
        //         border: `1px solid rgba(0,0,0,0.5)`,
        //         borderColor: 'rgba(0,0,0,0.5)',
        //     },
        //     '& focus': {
        //         border: `1px solid rgba(0,0,0,0.5)`,
        //         borderColor: 'rgba(0,0,0,0.5)',
        //     },
        // },
        imageInputWrap: {
            backgroundColor: COLOR.tertiary.music,
            width: theme.spacing(4),
            minWidth: theme.spacing(4),
            height: theme.spacing(4),
            display: 'grid',
            placeItems: 'center',
        },
        icon: {
            fontSize: theme.spacing(1),
            WebkitTextStrokeWidth: '1px',
        },
        imageInput: { display: 'none' },
        imgThumbWrap: {
            minWidth: theme.spacing(4),
            height: theme.spacing(4),
            position: 'relative',
            borderRadius: 4,
            overflow: 'hidden',
            '&:hover [id^="imageThumbOverlay"]': { display: 'grid' },
        },
        imgThumb: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
        },
        closeIconWrap: {
            position: 'absolute',
            inset: 0,
            zIndex: 5,
            backgroundColor: alpha(COLOR.tertiary.music, 0.4),
            display: 'none',
            placeItems: 'center',
        },
    });
});

export default ChatInput;
